import { SilverbackPageContext } from '@amazeelabs/gatsby-source-silverback';
import {
  buildHtml,
  buildLink,
  PageRoute,
  Route,
  RouteInput,
  RouteSlotInput,
  SeoRoute,
  ThemeProvider,
} from '@caritas/ui';
import { useLocation } from '@reach/router';
import { Script, ScriptStrategy } from 'gatsby';
import React, { PropsWithChildren } from 'react';

import {
  Donation,
  PovertyPlatformPage,
  SponsorshipDonation,
  StockDonation,
} from '../../generated/operation-ids';
import { backButtonStore } from '../commons/back';
import { useSiteSettings } from '../commons/site-settings';
import { currentUserStore } from '../commons/user';
import { useMenus } from '../commons/user-menus';
import { useTranslations } from '../fragments/Translations.mappers';
import { languageFallback, mapPageLocalizations } from './localizations';
import { scrollToHash } from './scrolling';
import { currentTheme } from './theme';

export type PageMetadata = Exclude<
  RouteSlotInput<typeof SeoRoute, 'contents'>,
  Function
>;

const mapPageHeader = (
  pageContext: SilverbackPageContext,
  donationPurpose?: (Donation & StockDonation & SponsorshipDonation) | {},
  donationDescription?: string,
  headerDisplayMode?: string,
  isPovertyPlatform?: boolean,
): RouteSlotInput<typeof PageRoute, 'header'> =>
  function usePageHeader() {
    const {
      menus: { meta, main, povertyMeta },
    } = useMenus(pageContext);
    const siteSettings = useSiteSettings(pageContext, donationPurpose);

    // We only want to show two levels for the main menu.
    // Also, we want to show the main menu items only if we are not in the
    // context of a poverty platform page.
    const mainMenu = main.items.map((firstLevel) => {
      return Object.assign(firstLevel, {
        children: firstLevel?.children?.map((secondLevel) => {
          // Remove third level menu items
          secondLevel.children = undefined;
          return secondLevel;
        }),
      });
    });

    return [
      {
        currentPath: useLocation().pathname,
        HomeLink: buildLink({
          href: `/${languageFallback(pageContext.locale || 'de')}`,
        }),
        localizations: mapPageLocalizations(pageContext, isPovertyPlatform),
        metaMenuItems: isPovertyPlatform ? povertyMeta.items : meta.items,
        SearchLink: siteSettings.siteSearchPage
          ? buildLink({ href: siteSettings.siteSearchPage.path })
          : undefined,
        mainMenuItems: mainMenu,
        DonationLink: buildLink({
          href: `${siteSettings.headerCtaPath}${
            donationDescription
              ? `?description=${encodeURIComponent(donationDescription)}`
              : ``
          }`,
        }),
        CertificateLink: buildLink({ href: 'https://zewo.ch/' }),
        countryProgram: {
          Link: buildLink({
            href: siteSettings.countryProgramme?.path,
            title: siteSettings.countryProgramme?.label,
          }),
          ariaLabel: siteSettings.countryProgramme?.label,
          linkTitle: siteSettings.countryProgramme?.label,
        },
        LoginLink:
          currentTheme === 'bergeinsatz'
            ? buildLink({ href: siteSettings.loginRegisterPage?.path })
            : undefined,
        ProfileLink:
          currentTheme === 'bergeinsatz'
            ? buildLink({ href: siteSettings.myProfilePage?.path })
            : undefined,
        currentUser: currentUserStore,
        microsite:
          (headerDisplayMode && headerDisplayMode === 'microsite') || false,
        backButton: backButtonStore,
        isPovertyPlatform,
      },
      200,
    ];
  };

export const mapPageFooter = (
  pageContext: SilverbackPageContext,
  headerDisplayMode?: string,
  isPovertyPlatform?: boolean,
): RouteSlotInput<typeof PageRoute, 'footer'> =>
  function usePageFooter() {
    const {
      menus: { footer },
    } = useMenus(pageContext);
    return [
      {
        HomeLink: buildLink({
          href: `/${languageFallback(pageContext.locale || 'de')}`,
        }),
        footerMenuItems: footer.items,
        localizations: mapPageLocalizations(pageContext),
        microsite:
          (headerDisplayMode && headerDisplayMode === 'microsite') || false,
        isPovertyPlatform: !!isPovertyPlatform,
      },
      200,
    ];
  };

export const mapPagePreFooter = (
  pageContext: SilverbackPageContext,
  isPovertyPlatform?: boolean,
): RouteSlotInput<typeof PageRoute, 'preFooter'> =>
  function usePagePreFooter() {
    const {
      menus: { quick, donation, social },
    } = useMenus(pageContext);
    const siteSettings = useSiteSettings(pageContext);

    return [
      {
        donationSection: {
          donationMenu: donation.items,
          iban: currentTheme !== 'youngcaritas' ? 'CH69 0900 0000 6000 7000&nbsp;4' : undefined,
          CertificateLink: buildLink({ href: 'https://zewo.ch/' }),
        },
        NewsletterForm: siteSettings.newsletterFormEmbedCode
          ? buildHtml(siteSettings.newsletterFormEmbedCode)
          : undefined,
        quickLinksMenu: quick.items,
        socialMenuLinks: social.items.map((item) => ({
          ...item,
          type: item.title.toLowerCase(),
        })),
        isPovertyPlatform: !!isPovertyPlatform,
      },
      200,
    ];
  };

function mapPage(
  pageContext: SilverbackPageContext,
  donationPurpose?: (Donation & StockDonation & SponsorshipDonation) | {},
  donationDescription?: string,
  headerDisplayMode?: string,
  povertyPlatform?: PovertyPlatformPage | {},
): RouteInput<typeof PageRoute> {
  return {
    header: mapPageHeader(
      pageContext,
      donationPurpose,
      donationDescription,
      headerDisplayMode,
      !!povertyPlatform,
    ),
    preFooter: mapPagePreFooter(pageContext, !!povertyPlatform),
    footer: mapPageFooter(pageContext, headerDisplayMode, !!povertyPlatform),
  };
}

export function PageHead({
  pageContext,
  pageMetadata,
  disableBrowserTranslation,
}: {
  pageContext: SilverbackPageContext;
  pageMetadata: PageMetadata;
  disableBrowserTranslation?: boolean;
}) {
  return (
    <Route
      definition={SeoRoute}
      input={{
        contents: pageMetadata,
      }}
      intl={useTranslations(pageContext)}
    >
      <Script
        dangerouslySetInnerHTML={{
          __html: `
            if (typeof window !== 'undefined') {
              const allowedLanguages = ['de', 'it', 'fr', 'en'];
              if (!allowedLanguages.includes(document.documentElement.getAttribute('lang'))) {
                let locale = window.location.pathname.split('/')[1];
                if (!allowedLanguages.includes(locale)) {
                  locale = 'de';
                }
                document.documentElement.setAttribute('lang', locale);
              }
            }
          `,
        }}
      />
      <Script
        src={`https://cse.google.com/cse.js?cx=${process.env.GATSBY_GOOGLE_SEARCH_ID}`}
      />
      <Script
      // The actual script will be loaded via a redirect in gatsby-node.ts
      // Putting a url here creates issues with netlify redirects, as the
      // characters are encoded and the redirect seems to just not work.
      // For now, this is loaded with the gatsby googletagmanager plugin.
      // @todo: Temporary disabled till we make the partytown work with gtm.
      //src={`googletagmanager_${process.env.GATSBY_GTMID}`}
      //strategy={ScriptStrategy.offMainThread}
      //forward={[`dataLayer.push`]}
      />
      <Script id="gtm-init" strategy={ScriptStrategy.offMainThread}>
        {`
          // @todo: Temporary disabled till we make the partytown work with gtm.
          //window.dataLayer = window.dataLayer || []
          //window.dataLayer.push({ 'gtm.start': new Date().getTime(), 'event': 'gtm.js' })
        `}
      </Script>
      <Script>
        {`
        var MTUserId = '3483acd8-7e6e-471c-be52-2077bd0d2e62';
        var MTFontIds = new Array();
      
        MTFontIds.push('5663746'); // Neue Helvetica® W05 77 Condensed Bold
        MTFontIds.push('5664085'); // Neue Helvetica® W05 45 Light
        MTFontIds.push('5664093'); // Neue Helvetica® W05 55 Roman
        MTFontIds.push('5664098'); // Neue Helvetica® W05 56 Italic
        MTFontIds.push('5664111'); // Neue Helvetica® W05 76 Bold Italic
        MTFontIds.push('5664150'); // Neue Helvetica® W05 75 Bold
        (function () {
          const mtTracking = document.createElement('script');
          mtTracking.type = 'text/javascript';
          mtTracking.async = 'true';
          mtTracking.src = '/mtiFontTrackingCode.js';
      
          (
            document.getElementsByTagName('head')[0] ||
            document.getElementsByTagName('body')[0]
          ).appendChild(mtTracking);
        })();
        `}
      </Script>
      <Script src={'https://js.convertflow.co/production/websites/6887.js'} />
      <Script type="text/javascript">{`function OptanonWrapper() { }`}</Script>
      {disableBrowserTranslation ? <meta name="googlebot" content="notranslate"></meta> : null}
      {disableBrowserTranslation ? <meta name="google" content="notranslate"></meta> : null}
    </Route>
  );
}

export function Page({
  pageContext,
  donationPurpose,
  donationDescription,
  headerDisplayMode,
  povertyPlatform,
  children,
  disableBrowserTranslation,
}: PropsWithChildren<{
  pageContext: SilverbackPageContext;
  donationPurpose?: (Donation & StockDonation & SponsorshipDonation) | {};
  donationDescription?: string;
  headerDisplayMode?: string;
  disableBrowserTranslation?: boolean;
  povertyPlatform?: PovertyPlatformPage | {};
}>) {
  scrollToHash();

  return (
    <div translate={disableBrowserTranslation ? "no" : "yes"}>
      <ThemeProvider theme={currentTheme}>
        <Route
          definition={PageRoute}
          input={mapPage(
            pageContext,
            donationPurpose,
            donationDescription,
            headerDisplayMode,
            povertyPlatform,
          )}
          intl={useTranslations(pageContext)}
        >
          {children}
        </Route>
      </ThemeProvider>
    </div>
  );
}
